import { CardDetailsDto, EmployeeDto } from '@zorro/clients';
import { CreditCardIcon } from '@zorro/shared/assets';
import { Box, Group, Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';

import { CreditCard } from '../CreditCard/CreditCard';
import { CreditCardSkeleton } from '../PaymentMethod/CreditCardSkeleton';
import { ZorroPayMaskedCardVisual } from './ZorroPayMaskedCardVisual';

const CARD_MAX_WIDTH = 439; // matches ZorroPayMaskedCardVisual's own footprint

type ZorroPayCardDetailsProps = {
  employeeId: EmployeeDto['id'];
  card: CardDetailsDto | null;
  isLoading: boolean;
  /**
   * Heading text — lets the ops view tell the two cards apart, since the card
   * face itself carries no marker (Story 3: identical face, role only in data).
   */
  label?: string;
};

type ZorroPayCardDetailsContentProps = {
  cardLastFour: string;
  expDate: string;
  employeeId: EmployeeDto['id'];
};

const ZorroPayCardDetailsContent = ({
  cardLastFour,
  expDate,
  employeeId,
}: ZorroPayCardDetailsContentProps) => {
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const nameOnCard = [employee?.firstName, employee?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <ZorroPayMaskedCardVisual
      expDate={expDate}
      cardLastFour={cardLastFour}
      nameOnCard={nameOnCard}
    />
  );
};

const ZorroPayCardDetails = ({
  employeeId,
  card,
  isLoading,
  label = 'Card details',
}: ZorroPayCardDetailsProps) => {
  return (
    <Stack gap="md">
      <Group gap="xs">
        <CreditCardIcon width="20" height="20" />
        <Text fw="var(--font-weight-600)" fz="var(--font-size-14)">
          {label}
        </Text>
      </Group>
      <Box
        pos="relative"
        w="100%"
        maw={439}
        miw={439}
        mih={276}
        pr={isLoading || !card || !card.isActive ? '0.675rem' : 0}
        style={{ alignSelf: 'flex-start' }}
      >
        {isLoading ? (
          <CreditCardSkeleton maxWidth={CARD_MAX_WIDTH} />
        ) : !card || !card.isActive ? (
          <CreditCard badgeType="not-available" maxWidth={CARD_MAX_WIDTH} />
        ) : (
          <ZorroPayCardDetailsContent
            cardLastFour={card.cardLastFour}
            expDate={card.expDate ?? ''}
            employeeId={employeeId}
          />
        )}
      </Box>
    </Stack>
  );
};

export { ZorroPayCardDetails };
